import EventEmitter from 'eventemitter3';
import { VideoOverlayPayload } from '@/scripts/store/modules/video/mutations/manageVideoPlayerOverlays/createOverlayHelper';
import { FilmQuotaManifest } from './manifest';
import { FilmQuotaManifestProvider } from './manifestProvider';
import { FilmQuotaStorageProvider, ConsumptionInfo } from './storageProvider';
import { CorruptedStorageValueError } from './storageProvider/storage';

export class UninitializedError extends Error {};
export class ConsumeError extends Error {};
export class QuotaExceededError extends ConsumeError {};

export enum ConsumptionChangeReason {
  /**
   * Consumption info is changed due to a quota being consumed programatically
   */
  Consumed = 'consumed',
  /**
   * Consumption info is reset due to expiry
   */
  Expired = 'expired',
  /**
   * Consumption info is reset due to corruption
   */
  Corrupted = 'corrupted',
}
export enum ConsumptionErrorReason {
  /**
   * Cannot consume because quota is exceeded
   */
  Exceeded = 'exceeded',

  Unknown = 'unknown',
}

export interface FilmQuotaServiceListeners {
  consumptionChange: (reason: ConsumptionChangeReason, newInfo: ConsumptionInfo, oldInfo: ConsumptionInfo) => void;
  consumptionError: (errorReason: ConsumptionErrorReason) => void;
}

export class FilmQuotaService extends EventEmitter<FilmQuotaServiceListeners> {
  private manifestProvider: FilmQuotaManifestProvider | null = null;

  private storageProvider: FilmQuotaStorageProvider | null = null;

  private async trackConsumptionChange(payload: { reason: ConsumptionChangeReason; oldInfo: ConsumptionInfo }): Promise<void> {
    if (!this.manifestProvider || !this.storageProvider) throw new UninitializedError();

    const newInfo = await this.storageProvider.getConsumptionInfo();

    this.emit('consumptionChange', payload.reason, newInfo, payload.oldInfo);
  }

  /**
   * Check if consumption info is expired.
   * If yes, reset consumption info. Otherwise, do nothing.
   *
   * @returns true if the consumption info is refreshed. false otherwise.
   */
  private async resetExpiredConsumptionInfo(): Promise<boolean> {
    if (!this.manifestProvider || !this.storageProvider) throw new UninitializedError();

    const info = await this.storageProvider.getConsumptionInfo();
    const firstConsumedAt = info.firstConsumedAt;

    if (firstConsumedAt == null) return false;

    const expiry = firstConsumedAt + this.manifestProvider.manifest.age;

    const isExpired = Date.now() > expiry;

    if (!isExpired) return false;

    await this.storageProvider.reset();
    await this.trackConsumptionChange({
      reason: ConsumptionChangeReason.Expired,
      oldInfo: info,
    });

    return true;
  }

  /**
   * Repair consumption info by resetting it to 0
   */
  private async handleCorruptedStorageValue(): Promise<void> {
    if (!this.manifestProvider || !this.storageProvider) throw new UninitializedError();

    const oldInfo = await this.storageProvider.reset();
    await this.trackConsumptionChange({
      reason: ConsumptionChangeReason.Corrupted,
      oldInfo,
    });
  }

  /**
   * Initialilze service. This will setup and manifest and storage component of this service.
   * Service must be initialized before calling its methods.
   *
   * @param manifest manifest description the quota and presentation information
   * @param namespace the key used for storing / retriving consumption info. Default to `"vs-fqs"`
   */
  async initialize(manifest: FilmQuotaManifest, namespace?: string): Promise<void> {
    this.manifestProvider = new FilmQuotaManifestProvider(manifest);
    this.storageProvider = new FilmQuotaStorageProvider(namespace);

    try {
      await this.resetExpiredConsumptionInfo();
    } catch (error) {
      if (error instanceof CorruptedStorageValueError) {
        await this.handleCorruptedStorageValue();
        return;
      }
      throw error;
    }
  }

  /**
   * Get the presentation information based on the quota consumption.
   * @returns null or a VideoOverlayPayload
   */
  async getPresentation(): Promise<VideoOverlayPayload | null> {
    if (!this.manifestProvider || !this.storageProvider) throw new UninitializedError();

    const consumptionInfo = await this.storageProvider.getConsumptionInfo();

    return this.manifestProvider.getPresentation(consumptionInfo.consumedCount);
  }

  /**
   * Consume a quota
   * @throws {QuotaExceededError} If attempting to consume quota when quoata has exceeded
   */
  async consume(): Promise<void> {
    if (!this.manifestProvider || !this.storageProvider) throw new UninitializedError();

    try {
      const consumptionInfo = await this.storageProvider.getConsumptionInfo();
      if (this.manifestProvider.isExceeded(consumptionInfo.consumedCount)) {
        throw new QuotaExceededError('Quota exceeded');
      };

      await this.storageProvider.consume();
      await this.trackConsumptionChange({
        reason: ConsumptionChangeReason.Consumed,
        oldInfo: consumptionInfo,
      });
    } catch (error) {
      if (error instanceof ConsumeError) {
        this.emit('consumptionError', ConsumptionErrorReason.Exceeded);
        throw error;
      }

      this.emit('consumptionError', ConsumptionErrorReason.Unknown);
    }
  }
}
